import { ChangeEvent, KeyboardEventHandler, useState } from "react";
import { FieldArray, Formik, useField, useFormikContext } from "formik";
import formModel from "../../ProjectWizardPages/formModel";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import InputField from "./InputField";
import StyledTablePagination from "../Tables/StyledTablePagination";
import * as Yup from "yup";
import SaveReminder from "../SaveReminder";
import { useTranslation } from "react-i18next";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import MultiSelectTagField from "./MultiSelectTagField";
import { Project } from "../../../types/project";
import { groupedActivityTags } from "../../../helpers/selectOptions";
import AddIcon from '@mui/icons-material/Add';
type ActivitiesFieldProps = {};

export default function ActivitiesField({ ...rest }: ActivitiesFieldProps) {
  const { t } = useTranslation();
  // eslint-disable-next-line
  const [_, meta] = useField("activities");
  const { values } = useFormikContext<Project>();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const {
    formField: { activities },
  } = formModel;

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined
  ) => {
    if (event?.target.value) {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    }
  };
  const activityValidation = Yup.object().shape({
    activity: Yup.string()
      .required("errors.required")
      .max(32, "errors.max32")
      .test("Test for duplicates", "errors.duplicate", (value) => {
        let dub = false;
        values.activities.forEach((element) => {
          if (!value) return;
          if (element.activity.toLowerCase() === value.toLowerCase()) {
            dub = true;
          }
        });
        if (dub) {
          return false;
        } else return true;
      }),
  });

  return (
    <Box>
      <Box mt={2} {...rest}>
        <FieldArray
          name="activities"
          render={({ remove, unshift }) => (
            <>
              <List disablePadding>
                <>
                  <Formik
                    initialValues={{
                      activity: "",
                      tags: [],
                    }}
                    validationSchema={activityValidation}
                    validateOnBlur={false}
                    onSubmit={(values, actions) => {
                      unshift(values);
                      actions.resetForm();
                    }}
                  >
                    {(subForm) => (
                      <ListItem
                        disableGutters
                        key={"activities"}
                        secondaryAction={
                          <SaveReminder>
                             <IconButton
                               onClick={() => subForm.submitForm()}
                               edge="end"
                            >
                              <AddIcon color={"primary"} />
                            </IconButton>  
                          </SaveReminder>
                        }
                      >
                        <ActivityField
                          tagsName={"tags"}
                          activityName={"activity"}
                    
                          label={activities.label}
                          placeholder={activities.placeholder}
                          onKeyPress={(e: any) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              subForm.submitForm();
                            }
                          }}
                        />
                      </ListItem>
                    )}
                  </Formik>
                  {meta &&
                    meta.error &&
                    meta.touched &&
                    Array.isArray(meta.error) === false && (
                      <Typography color={"red"}>{t(meta.error)}</Typography>
                    )}
                  <Divider sx={{ marginBottom: "50px", marginTop: "20px" }} />
                </>

                {values.activities.length > 0
                  ? (rowsPerPage > 0
                      ? values.activities.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                      : values.activities
                    ).map((_, index) => {
                      return (
                        <ListItem
                          disableGutters
                          key={index}
                          secondaryAction={
                            <IconButton
                              onClick={() => remove(index)}
                              edge="end"
                              title={t("labels.delete")}
                            >
                              <DeleteIcon color={"primary"} />
                            </IconButton>
                          }
                        >
                          <ActivityField
                            tagsName={`activities[${index}].tags`}
                            activityName={`activities[${index}].activity`}
                            label={activities.label}
                            placeholder={activities.placeholder}
                            isEdit={true}
                          />
                        </ListItem>
                      );
                    })
                  : null}
              </List>
            </>
          )}
        />
      </Box>
      {values.activities.length > 10 && (
        <StyledTablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          count={values.activities.length}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}
    </Box>
  );
}

type ActivityFieldProps = {
  activityName: string;
  tagsName: string;
  label: string;
  placeholder: string;
  isEdit?: boolean;
  onKeyPress?: KeyboardEventHandler<HTMLDivElement>;
  sx?: SxProps<Theme> | undefined;
};

const ActivityField = ({
  activityName,
  tagsName,
  label,
  placeholder,
  isEdit,
  onKeyPress,
  sx,
}: ActivityFieldProps) => {
  const { t } = useTranslation();
  const [field] = useField(tagsName);
  const [open, setOpen] = useState(false);

  const handleClose = () => setOpen(false);

  return (
    <>
      <InputField
        onKeyDown={onKeyPress}
        sx={sx}
        name={activityName}
        label={label}
        placeholder={placeholder}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography mr={1}>
                {field.value && field.value.length} tags
              </Typography>

              <Button
                onClick={() => setOpen(true)}
                startIcon={<AddCircleOutlineIcon />}
              >
                {isEdit ? t("labels.editTag") : t("labels.addTags")}
              </Button>
            </InputAdornment>
          ),
        }}
        InputLabelProps={{
          shrink: true,
        }}
      />
      <MultiSelectTagField
        sx={{
          clip: "rect(0 0 0 0)",
          clipPath: "inset(50%)",
          height: 1,
          overflow: "hidden",
          position: "absolute",
          bottom: 0,
          left: 0,
          whiteSpace: "nowrap",
          width: 1,
        }}
        isFullWidth
        isDisabled={false}
        openSelect={open}
        onClose={handleClose}
        label={"labels.tags"}
        name={tagsName}
        data={groupedActivityTags.filter(
          (group) => group.header !== "tags.Back" && group.header !== "tags.Elbow"
        )}
      />
    </>
  );
};
